export const SKILL_PACKAGE_TOP_DIRS = ["references", "templates", "examples"] as const;
export type SkillPackageTopDir = (typeof SKILL_PACKAGE_TOP_DIRS)[number];

const ALLOWED_TOP_DIRS = SKILL_PACKAGE_TOP_DIRS;
const FORBIDDEN_DIR_SEGMENTS = ["scripts", "assets"] as const;
const ALLOWED_EXTENSIONS = [
  ".md",
  ".markdown",
  ".txt",
  ".json",
  ".yaml",
  ".yml",
  ".csv",
  ".toml",
  ".xml",
] as const;

export const HERMES_SKILL_ROOT_FILE = "SKILL.md";

export const DEFAULT_NEW_SKILL_BODY = `---
name: my-skill
description: 一句话说明这个技能帮助手做什么
---

# 我的技能

在此编写工作协议正文…
`;

/** 是否允许在 UI 中创建/保留的路径（不含 scripts/assets）。 */
export function isAllowedSkillPackagePath(path: string): boolean {
  const normalized = normalizeSkillPath(path);
  if (!normalized) return false;

  const segments = normalized.split("/");
  if (segments.some((segment) => FORBIDDEN_DIR_SEGMENTS.includes(segment as (typeof FORBIDDEN_DIR_SEGMENTS)[number]))) {
    return false;
  }

  if (normalized === HERMES_SKILL_ROOT_FILE) return true;

  const topDir = segments[0];
  if (!ALLOWED_TOP_DIRS.includes(topDir as (typeof ALLOWED_TOP_DIRS)[number])) return false;

  const ext = extensionOf(normalized);
  return ext ? ALLOWED_EXTENSIONS.includes(ext as (typeof ALLOWED_EXTENSIONS)[number]) : false;
}

export function normalizeSkillPath(path: string): string | null {
  let text = path.trim().replace(/\\/g, "/");
  if (!text) return null;
  text = text.replace(/^\/+/, "");
  while (text.startsWith("./")) text = text.slice(2);

  const segments = text.split("/").filter(Boolean);
  if (segments.some((segment) => segment === "..")) return null;
  if (segments.length === 0) return null;
  return segments.join("/");
}

function extensionOf(path: string): string | null {
  const dot = path.lastIndexOf(".");
  if (dot <= 0) return null;
  return path.slice(dot).toLowerCase();
}

/** 建议下一个可添加的附属文件路径（按白名单顶层目录）。 */
export function suggestNewSkillFilePath(
  existingPaths: string[],
  topDir: SkillPackageTopDir = "references",
): string {
  let index = 1;
  while (existingPaths.includes(`${topDir}/note-${index}.md`)) {
    index += 1;
  }
  return `${topDir}/note-${index}.md`;
}

/** 将 path 列表整理为文件树节点（仅一层目录 + 根文件）。 */
export type SkillFileTreeNode =
  | { kind: "file"; path: string; name: string }
  | { kind: "dir"; name: string; children: SkillFileTreeNode[] };

export function buildSkillFileTree(paths: string[]): SkillFileTreeNode[] {
  const rootFiles: SkillFileTreeNode[] = [];
  const dirs = new Map<string, string[]>();

  for (const path of paths.sort()) {
    if (!path.includes("/")) {
      rootFiles.push({ kind: "file", path, name: path });
      continue;
    }
    const [dir, ...rest] = path.split("/");
    const fileName = rest.join("/");
    const list = dirs.get(dir) ?? [];
    list.push(fileName);
    dirs.set(dir, list);
  }

  const dirNodes: SkillFileTreeNode[] = [];
  for (const [name, files] of Array.from(dirs.entries()).sort(([a], [b]) => a.localeCompare(b))) {
    dirNodes.push({
      kind: "dir",
      name,
      children: files.sort().map((fileName) => {
        const path = `${name}/${fileName}`;
        return { kind: "file" as const, path, name: fileName };
      }),
    });
  }

  return [...rootFiles, ...dirNodes];
}

export function computeSkillPackageChanges(
  current: Record<string, string>,
  saved: Record<string, string>,
): { filesUpsert: Array<{ path: string; body: string }>; filesDelete: string[] } {
  const filesUpsert: Array<{ path: string; body: string }> = [];
  const filesDelete: string[] = [];

  for (const [path, body] of Object.entries(current)) {
    if (saved[path] !== body) {
      filesUpsert.push({ path, body });
    }
  }

  for (const path of Object.keys(saved)) {
    if (!(path in current)) {
      filesDelete.push(path);
    }
  }

  return { filesUpsert, filesDelete };
}

export function skillPackageIsDirty(
  current: Record<string, string>,
  saved: Record<string, string>,
): boolean {
  const keys = new Set([...Object.keys(current), ...Object.keys(saved)]);
  for (const path of keys) {
    if ((current[path] ?? "") !== (saved[path] ?? "")) return true;
  }
  return false;
}
